import React, { useEffect } from 'react';
import { Button, Modal, Row, Col, Badge, Card, Alert } from 'react-bootstrap';
import { ErrorMessage, Field, Form, Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { formatarMonetario } from '~/Utils/Helpers.js';
import dialog from '~/Utils/dialog';
import MoneyField from '~/Components/Form/MoneyField';
import {
  calcularValorChequeBonus,
  calcularValorSinalEsperado,
  TiposAplicacaoChequeBonus,
  TiposValorChequeBonus,
  TiposEntrada,
  r2,
  calcularValorSinalIncorporadoraEsperado,
  recalcularValoresProporcionais,
} from '~/Pages/Vendas/helpers';
import { CondicoesEspeciaisStatus, TipoComissao } from '~/constant';
import AuthStorage from '~/Utils/AuthStorage';

const definirObjetoSinal = (comissaoEmpreendimento, values, ativoEmEdicao) => {
  if (comissaoEmpreendimento === TipoComissao.RetidaCorretor) {
    if (ativoEmEdicao.ge_vbp === 'S') {
      return {
        titulo: 'Sinal Imobiliária',
        nome: 'valor_sinal_imobiliaria',
        valor: values.valor_sinal_imobiliaria,
      };
    }

    if (ativoEmEdicao.valor_sinal_incorporadora_original > 0) {
      return {
        titulo: 'Sinal Incorporadora',
        nome: 'valor_sinal_incorporadora',
        valor: values.valor_sinal_incorporadora,
      };
    }

    return {
      titulo: 'Sinal Imobiliária',
      nome: 'valor_sinal_imobiliaria',
      valor: values.valor_sinal_imobiliaria,
    };
  }
  return {
    titulo: 'Valor Sinal',
    nome: 'valor_sinal',
    valor: values.valor_sinal,
  };
};

const modificarSinalIncorporadora = (comissaoEmpreendimento, ativoEmEdicao) => {
  return (
    comissaoEmpreendimento === TipoComissao.RetidaCorretor &&
    ativoEmEdicao.valor_sinal_incorporadora_original > 0 &&
    ativoEmEdicao.ge_vbp === 'N'
  );
};

const VerificacaoValores = ({
  ativoEmEdicao,
  chequeBonus,
  comissaoEmpreendimento,
}) => {
  const form = useFormikContext();

  useEffect(() => {
    verificaValorVenda(form.values.valor_venda);
  }, [form.values.valor_venda]);

  const obterChaveValue = () => {
    if (comissaoEmpreendimento === TipoComissao.RetidaCorretor) {
      if (ativoEmEdicao.ge_vbp === 'S') {
        return 'valor_sinal_imobiliaria';
      }
      if (ativoEmEdicao.valor_sinal_incorporadora_original > 0) {
        return 'valor_sinal_incorporadora';
      }
      return 'valor_sinal_imobiliaria';
    }
    return 'valor_sinal';
  };

  useEffect(() => {
    verificaValorSinal(form.values[obterChaveValue()]);
  }, [form.values[obterChaveValue()]]);

  const verificaValorSinal = valorSinal => {
    const { valor_venda, aplicou_cheque_bonus_sinal } = form.values;

    const objetoSinal = definirObjetoSinal(
      comissaoEmpreendimento,
      form.values,
      ativoEmEdicao
    );

    let valorSinalEsperado = modificarSinalIncorporadora(
      comissaoEmpreendimento,
      ativoEmEdicao
    )
      ? r2(calcularValorSinalIncorporadoraEsperado(ativoEmEdicao, valor_venda))
      : r2(calcularValorSinalEsperado(ativoEmEdicao, valor_venda));

    let valorInformadoOuFixo =
      valorSinal > valorSinalEsperado ? valorSinalEsperado : valorSinal;

    form.setFieldValue(objetoSinal.nome, valorInformadoOuFixo);

    let descontoSinal = r2(valorSinalEsperado - valorInformadoOuFixo);
    if (descontoSinal < 0) descontoSinal = 0;
    if (aplicou_cheque_bonus_sinal && descontoSinal < chequeBonus.valor) {
      descontoSinal = chequeBonus.valor;
      form.setFieldValue(
        objetoSinal.nome,
        valorSinalEsperado - chequeBonus.valor
      );
    }
    form.setFieldValue('desconto_sinal', descontoSinal);
  };

  const verificaValorVenda = valorVenda => {
    const { valor_venda_original } = ativoEmEdicao;
    const valorVendaInformadoOuOriginal = r2(valorVenda);
    form.setFieldValue('valor_venda', valorVendaInformadoOuOriginal);
    let descontoValorVenda = r2(
      valor_venda_original - valorVendaInformadoOuOriginal
    );
    form.setFieldValue('desconto_valor_venda', descontoValorVenda);

    // Recalcula proporcionalmente os valores de sinal da imobiliária e incorporadora
    // APENAS quando não for VBP ou quando for comissão diferente de RetidaCorretor
    const deveRecalcularProporcionalmente =
      comissaoEmpreendimento !== TipoComissao.RetidaCorretor ||
      ativoEmEdicao.ge_vbp !== 'S';

    if (deveRecalcularProporcionalmente) {
      const valoresRecalculados = recalcularValoresProporcionais(
        ativoEmEdicao,
        valorVendaInformadoOuOriginal
      );

      // Atualiza os valores recalculados no formulário
      form.setFieldValue(
        'valor_sinal_imobiliaria',
        valoresRecalculados.valor_sinal_imobiliaria
      );
      form.setFieldValue(
        'valor_sinal_incorporadora',
        valoresRecalculados.valor_sinal_incorporadora
      );
    }

    const valorSinalEsperado = modificarSinalIncorporadora(
      comissaoEmpreendimento,
      ativoEmEdicao
    )
      ? r2(
          calcularValorSinalIncorporadoraEsperado(
            ativoEmEdicao,
            valorVendaInformadoOuOriginal
          )
        )
      : r2(
          calcularValorSinalEsperado(
            ativoEmEdicao,
            valorVendaInformadoOuOriginal
          )
        );
    form.setFieldValue('valor_sinal_esperado', valorSinalEsperado);
    verificaValorSinal(valorSinalEsperado);
  };

  return null;
};

const validarDescontoPercentual = (
  valorOriginal,
  valorInformado,
  descontoMaximo,
  contexto,
  rotuloCampo
) => {
  if (!descontoMaximo || descontoMaximo <= 0) return true;
  if (!valorOriginal || valorOriginal <= 0) return true;
  const percentual =
    ((valorOriginal - (valorInformado || 0)) / valorOriginal) * 100;
  if (percentual > descontoMaximo) {
    const valorMinimoPermitido = valorOriginal * (1 - descontoMaximo / 100);
    return contexto.createError({
      message: `Desconto sobre ${rotuloCampo} (${percentual
        .toFixed(2)
        .replace('.', ',')}%) excede o limite permitido de ${descontoMaximo}%. Valor mínimo permitido: ${formatarMonetario(
        valorMinimoPermitido,
        false
      )}.`,
    });
  }
  return true;
};

const construirValidationSchema = (
  descontoMaximo,
  valorVendaOriginal,
  valorSinalOriginal,
  valorSinalImobiliariaOriginal,
  valorSinalIncorporadoraOriginal
) =>
  Yup.object().shape({
    valor_venda: Yup.number().test(
      'desconto-maximo-venda',
      '',
      function(valor) {
        return validarDescontoPercentual(
          valorVendaOriginal,
          valor,
          descontoMaximo,
          this,
          'o valor da venda'
        );
      }
    ),
    valor_sinal: Yup.number().test('desconto-maximo-sinal', '', function(
      valor
    ) {
      return validarDescontoPercentual(
        valorSinalOriginal,
        valor,
        descontoMaximo,
        this,
        'o sinal'
      );
    }),
    valor_sinal_imobiliaria: Yup.number().test(
      'desconto-maximo-sinal-imob',
      '',
      function(valor) {
        return validarDescontoPercentual(
          valorSinalImobiliariaOriginal,
          valor,
          descontoMaximo,
          this,
          'o sinal imobiliária'
        );
      }
    ),
    valor_sinal_incorporadora: Yup.number().test(
      'desconto-maximo-sinal-incorp',
      '',
      function(valor) {
        return validarDescontoPercentual(
          valorSinalIncorporadoraOriginal,
          valor,
          descontoMaximo,
          this,
          'o sinal incorporadora'
        );
      }
    ),
  });

const ModalSinal = ({
  onCancel,
  ativoEmEdicao,
  salvarSinal,
  chequeBonus,
  ativosComChequeBonus,
  readonly,
  temParcelasSinal,
  condicoesEspeciais,
  comissaoEmpreendimento,
  descontoMaximo,
}) => {
  if (!ativoEmEdicao) return null;

  const {
    valor_venda_original,
    valor_sinal_original,
    valor_sinal_imobiliaria_original,
    valor_sinal_incorporadora_original,
    ge_vbp,
    desconto,
  } = ativoEmEdicao;

  const valorVendaFormatado = formatarMonetario(valor_venda_original);
  const valorSinalFormatado = formatarMonetario(valor_sinal_original);

  const {
    aplicou_cheque_bonus_sinal,
    aplicou_cheque_bonus_valor_venda,
    desconto_sinal,
    desconto_valor_venda,
  } = desconto || {};

  const chequeBonusAplicadoEmOutroLote =
    ativosComChequeBonus.length > 0 &&
    !ativosComChequeBonus.find(ativo => ativo.codigo === ativoEmEdicao.codigo);

  const printChequeBonus = (chequeBonus, valorBase) => {
    if (!chequeBonus) return null;

    const valorAplicadoCB = calcularValorChequeBonus(chequeBonus, valorBase);
    if (chequeBonus.tipo_valor_cheque_bonus === TiposValorChequeBonus.Fixo)
      return formatarMonetario(valorAplicadoCB);
    //porcentagem
    return `${chequeBonus.valor.toFixed(2)}% (${formatarMonetario(
      valorAplicadoCB
    )})`;
  };

  const aplicacaoChequeBonus = (chequeBonus, valorBase) => {
    if (!chequeBonus) return valorBase;
    if (chequeBonus.tipo_valor_cheque_bonus === TiposValorChequeBonus.Fixo)
      return valorBase - chequeBonus.valor;
    //porcentagem
    return valorBase - valorBase * chequeBonus.valor;
  };

  const bloquearModificacaoSinal = () => {
    if (comissaoEmpreendimento === TipoComissao.ComissaoContrato2) {
      return false;
    }
    if (ge_vbp === 'N' && valor_sinal_incorporadora_original === 0) {
      return true;
    }
    return false;
  };

  const habilitarManipulacaoChequeBonus = () => {
    if (
      comissaoEmpreendimento === TipoComissao.RetidaCorretor &&
      ativoEmEdicao.valor_sinal_incorporadora_original === 0
    ) {
      return false;
    }
    return true;
  };

  return (
    <Modal show={!!ativoEmEdicao} onHide={onCancel} backdrop="static">
      <Formik
        initialValues={{
          codigo: ativoEmEdicao.codigo,
          valor_sinal: r2(ativoEmEdicao.valor_sinal || valor_sinal_original),
          valor_sinal_incorporadora: r2(
            ativoEmEdicao.valor_sinal_incorporadora
          ),
          valor_sinal_imobiliaria: r2(ativoEmEdicao.valor_sinal_imobiliaria),
          valor_venda: r2(ativoEmEdicao.valor_venda || valor_venda_original),
          valor_sinal_esperado: r2(valor_sinal_original),
          valor_sinal_incorporadora_esperado: r2(
            valor_sinal_incorporadora_original
          ),
          aplicou_cheque_bonus_sinal: aplicou_cheque_bonus_sinal,
          aplicou_cheque_bonus_valor_venda: aplicou_cheque_bonus_valor_venda,
          comissao_esperada: r2(valor_sinal_original),
          desconto_sinal: desconto_sinal,
          desconto_valor_venda: desconto_valor_venda,
          comissao_empreendimento: comissaoEmpreendimento,
        }}
        enableReinitialization
        validationSchema={construirValidationSchema(
          descontoMaximo,
          valor_venda_original,
          valor_sinal_original,
          valor_sinal_imobiliaria_original,
          valor_sinal_incorporadora_original
        )}
        onSubmit={async values => {
          if (temParcelasSinal) {
            const result = await dialog.confirm({
              title: 'Editar valores do ativo?',
              message:
                'Ao editar os valores do ativo, as parcelas de sinal informadas serão removidas. Deseja continuar?',
            });
            if (!result.proceed) return;
          }
          salvarSinal(values);
          onCancel();
        }}
      >
        {({ values, setFieldValue }) => {
          const objetoSinal = definirObjetoSinal(
            comissaoEmpreendimento,
            values,
            ativoEmEdicao
          );
          return (
            <Form>
              <Modal.Header closeButton>
                <Modal.Title>{`Valores do lote #${ativoEmEdicao.codigo}`}</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <p>{`Quadra: ${ativoEmEdicao.quadra} / Lote: ${ativoEmEdicao.lote}`}</p>
                <Row className="mb-2">
                  <Col md={4}>
                    <SmallTitleValue
                      title="Valor original"
                      value={formatarMonetario(
                        ativoEmEdicao.valor_venda_original
                      )}
                    />
                  </Col>
                  <Col md={4}>
                    <SmallTitleValue
                      title="Sinal original"
                      value={formatarMonetario(
                        ativoEmEdicao.valor_sinal_original
                      )}
                    />
                  </Col>
                  <Col md={4}>
                    <SmallTitleValue
                      title="Entrada mínima"
                      value={formatarMonetario(
                        values.valor_sinal_esperado,
                        false
                      )}
                    />
                  </Col>
                </Row>
                <Card className="mb-2 card-small" bg="light">
                  <Card.Header>Valor da Venda</Card.Header>
                  <Card.Body>
                    {chequeBonus &&
                      chequeBonus.tipo_aplicacao_cheque_bonus ===
                        TiposAplicacaoChequeBonus.ValorVenda && (
                        <Alert variant="success">
                          <div className="d-flex justify-content-between align-items-center">
                            <span>
                              Cheque bônus:
                              <strong className="ml-2">
                                {printChequeBonus(
                                  chequeBonus,
                                  ativoEmEdicao.valor_venda_original
                                )}
                              </strong>
                            </span>

                            {values.aplicou_cheque_bonus_valor_venda && (
                              <Badge variant="success" className="float-right">
                                Aplicado
                              </Badge>
                            )}
                            {chequeBonusAplicadoEmOutroLote && (
                              <Badge variant="warning">
                                Aplicado em outro lote
                              </Badge>
                            )}

                            {!chequeBonusAplicadoEmOutroLote &&
                              !values.aplicou_cheque_bonus_valor_venda && (
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="float-right"
                                  onClick={() => {
                                    setFieldValue(
                                      'valor_venda',
                                      aplicacaoChequeBonus(
                                        chequeBonus,
                                        ativoEmEdicao.valor_venda_original
                                      )
                                    );
                                    setFieldValue(
                                      'aplicou_cheque_bonus_valor_venda',
                                      true
                                    );
                                  }}
                                >
                                  Aplicar
                                </Button>
                              )}
                            {values.aplicou_cheque_bonus_valor_venda && (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => {
                                  setFieldValue(
                                    'valor_venda',
                                    ativoEmEdicao.valor_venda_original
                                  );
                                  setFieldValue(
                                    'aplicou_cheque_bonus_valor_venda',
                                    false
                                  );
                                }}
                              >
                                Remover
                              </Button>
                            )}
                          </div>
                        </Alert>
                      )}

                    <Row>
                      <Col md="6">
                        <div className="form-group">
                          <label htmlFor="valor_venda">Valor da venda</label>
                          <Field
                            name="valor_venda"
                            id="valor_venda"
                            type="text"
                            component={MoneyField}
                            className="form-control"
                            placeholder={valorVendaFormatado}
                            disabled={
                              readonly ||
                              Object.keys(condicoesEspeciais).length > 0
                            }
                          />
                          <ErrorMessage
                            name="valor_venda"
                            component="small"
                            className="text-danger d-block"
                          />
                        </div>
                      </Col>
                      <Col md="6">
                        {values.desconto_valor_venda > 0 && (
                          <>
                            <small> Desconto total</small>
                            <strong className="d-block text-success">
                              {formatarMonetario(values.desconto_valor_venda)}
                            </strong>
                          </>
                        )}
                        {values.desconto_valor_venda < 0 && (
                          <>
                            <small> Acréscimo total</small>
                            <strong className="d-block text-warning">
                              {formatarMonetario(
                                Math.abs(values.desconto_valor_venda)
                              )}
                            </strong>
                          </>
                        )}
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
                <Card className="card-small" bg="light">
                  <Card.Header>Modificar Sinal</Card.Header>
                  <Card.Body>
                    {chequeBonus &&
                      chequeBonus.tipo_aplicacao_cheque_bonus ===
                        TiposAplicacaoChequeBonus.ValorSinal &&
                      habilitarManipulacaoChequeBonus() && (
                        <Alert variant="success">
                          <div className="d-flex justify-content-between align-items-center">
                            <span>
                              Cheque bônus:
                              <strong className="ml-2">
                                {printChequeBonus(
                                  chequeBonus,
                                  ativoEmEdicao.valor_sinal_original
                                )}
                              </strong>
                            </span>

                            {values.aplicou_cheque_bonus_sinal && (
                              <Badge variant="success" className="float-right">
                                Aplicado
                              </Badge>
                            )}
                            {chequeBonusAplicadoEmOutroLote && (
                              <Badge variant="warning">
                                Aplicado em outro lote
                              </Badge>
                            )}
                            {!readonly && (
                              <>
                                {!chequeBonusAplicadoEmOutroLote &&
                                  !values.aplicou_cheque_bonus_sinal && (
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      className="float-right"
                                      onClick={() => {
                                        const objetoSinal = definirObjetoSinal(
                                          comissaoEmpreendimento,
                                          values,
                                          ativoEmEdicao
                                        );
                                        setFieldValue(
                                          objetoSinal.nome,
                                          aplicacaoChequeBonus(
                                            chequeBonus,

                                            comissaoEmpreendimento ===
                                              TipoComissao.ComissaoContrato2
                                              ? values.valor_sinal_esperado
                                              : values.valor_sinal_incorporadora_esperado
                                          )
                                        );
                                        setFieldValue(
                                          'aplicou_cheque_bonus_sinal',
                                          true
                                        );
                                      }}
                                    >
                                      Aplicar
                                    </Button>
                                  )}
                                {values.aplicou_cheque_bonus_sinal && (
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => {
                                      const objetoSinal = definirObjetoSinal(
                                        comissaoEmpreendimento,
                                        values,
                                        ativoEmEdicao
                                      );
                                      setFieldValue(
                                        objetoSinal.nome,
                                        comissaoEmpreendimento ===
                                          TipoComissao.ComissaoContrato2
                                          ? values.valor_sinal_esperado
                                          : values.valor_sinal_incorporadora_esperado
                                      );
                                      setFieldValue(
                                        'aplicou_cheque_bonus_sinal',
                                        false
                                      );
                                    }}
                                  >
                                    Remover
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </Alert>
                      )}
                    <Row>
                      <Col md="6">
                        <div className="form-group">
                          <label htmlFor={objetoSinal.nome}>
                            {objetoSinal.titulo}
                          </label>
                          <Field
                            id={objetoSinal.nome}
                            type="tel"
                            placeholder={valorSinalFormatado}
                            name={objetoSinal.nome}
                            value={objetoSinal.valor}
                            component={MoneyField}
                            className="form-control"
                            disabled={
                              (readonly &&
                                !(
                                  condicoesEspeciais &&
                                  condicoesEspeciais.status
                                )) ||
                              (readonly &&
                                condicoesEspeciais &&
                                condicoesEspeciais.status !=
                                  CondicoesEspeciaisStatus.PendenteDiretoria) ||
                              (readonly &&
                                condicoesEspeciais &&
                                condicoesEspeciais.status ==
                                  CondicoesEspeciaisStatus.PendenteDiretoria &&
                                AuthStorage.isGerente()) ||
                              values.aplicou_cheque_bonus_sinal ||
                              bloquearModificacaoSinal()
                            }
                          />
                          <ErrorMessage
                            name={objetoSinal.nome}
                            component="small"
                            className="text-danger d-block"
                          />
                        </div>
                      </Col>
                      <Col md="6">
                        <small>Desconto total</small>
                        <strong className="d-block">
                          {values.desconto_sinal > 0
                            ? formatarMonetario(values.desconto_sinal)
                            : '-'}
                        </strong>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
                <VerificacaoValores
                  ativoEmEdicao={ativoEmEdicao}
                  chequeBonus={chequeBonus}
                  comissaoEmpreendimento={comissaoEmpreendimento}
                />
              </Modal.Body>
              <Modal.Footer className="">
                {readonly &&
                condicoesEspeciais &&
                condicoesEspeciais.status !=
                  CondicoesEspeciaisStatus.PendenteDiretoria ? (
                  <Button
                    variant="outline-primary"
                    type="button"
                    onClick={onCancel}
                  >
                    Fechar
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="primary"
                      className="mb-1"
                      type="submit"
                      disabled={readonly}
                    >
                      Salvar
                    </Button>
                    <Button
                      variant="outline-primary"
                      type="button"
                      onClick={onCancel}
                    >
                      Cancelar
                    </Button>
                  </>
                )}
              </Modal.Footer>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

const SmallTitleValue = ({ title, value }) => {
  return (
    <small>
      <strong className="d-block">{title}:</strong>
      {value}
    </small>
  );
};

export default ModalSinal;
